import {
  Download,
  Edit2,
  Eye,
  FileText,
  Filter,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import ImageWithActions from "../components/ImageWithActions";
import api from "../services/api";

const DirectoryList = ({ user, API_BASE_URL, getAuthHeader, logout }) => {
  const [directories, setDirectories] = useState([]);
  const [filteredDirectories, setFilteredDirectories] = useState([]);
  const [batchFilter, setBatchFilter] = useState("");
  const [bloodGroupFilter, setBloodGroupFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const navigate = useNavigate();

  // Check screen width
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth < 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchDirectories = async () => {
    setRefreshing(true);
    try {
      const data = await api.directory.getAll();
      setDirectories(data);
      setFilteredDirectories(data);
    } catch (error) {
      console.error("Error fetching directories:", error);
      showNotification(error.message || "Failed to load directories", "error");
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDirectories();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = directories;

    if (batchFilter) {
      filtered = filtered.filter((d) => d.batch === batchFilter);
    }

    if (bloodGroupFilter) {
      filtered = filtered.filter((d) => d.bloodGroup === bloodGroupFilter);
    }

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter((d) =>
        Object.values({
          name: d.name,
          batch: d.batch,
          mobile: d.mobile,
          email: d.email,
          profession: d.profession,
          officeAddress: d.officeAddress,
          permanentAddress: d.permanentAddress,
          bloodGroup: d.bloodGroup,
        })
          .join(" ")
          .toLowerCase()
          .includes(lowerSearch)
      );
    }

    setFilteredDirectories(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [directories, batchFilter, bloodGroupFilter, searchTerm]);

  const uniqueBatches = [
    ...new Set(directories.map((d) => d.batch).filter(Boolean)),
  ];
  const uniqueBloodGroups = [
    ...new Set(directories.map((d) => d.bloodGroup).filter(Boolean)),
  ];

  const showNotification = (message, type = "info") => {
    const existingNotification = document.querySelector(".custom-notification");
    if (existingNotification) {
      existingNotification.remove();
    }

    const notification = document.createElement("div");
    notification.className = `custom-notification alert alert-${
      type === "error" ? "danger" : type === "success" ? "success" : "info"
    }`;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      min-width: 300px;
      max-width: 500px;
      padding: 15px;
      border-radius: 5px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      animation: slideIn 0.3s ease-out;
    `;

    notification.innerHTML = `
      <div class="d-flex justify-content-between align-items-start">
        <div>
          <strong>${
            type === "error"
              ? "❌ Error"
              : type === "success"
              ? "✅ Success"
              : "ℹ️ Info"
          }</strong>
          <div class="mt-1">${message}</div>
        </div>
        <button class="btn btn-sm btn-link text-dark" onclick="this.parentElement.parentElement.remove()">
          ×
        </button>
      </div>
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 5000);
  };

  const downloadExcel = () => {
    // Use all filtered directories, not just paginated ones
    const ws = XLSX.utils.json_to_sheet(
      filteredDirectories.map((d) => ({
        ID: d.id,
        Name: d.name,
        Batch: d.batch || "N/A",
        Mobile: d.mobile || "N/A",
        Email: d.email || "N/A",
        Profession: d.profession || "N/A",
        "Office Address": d.officeAddress || "N/A",
        "Permanent Address": d.permanentAddress || "N/A",
        "Blood Group": d.bloodGroup || "N/A",
        "Professional Details": d.professionalDetails || "N/A",
        "Image URL": d.imageUrl ? `${API_BASE_URL}${d.imageUrl}` : "N/A",
      }))
    );

    // Adjust column widths
    const wscols = [
      { wch: 5 }, // ID
      { wch: 20 }, // Name
      { wch: 15 }, // Batch
      { wch: 15 }, // Mobile
      { wch: 25 }, // Email
      { wch: 20 }, // Profession
      { wch: 30 }, // Office Address
      { wch: 30 }, // Permanent Address
      { wch: 12 }, // Blood Group
      { wch: 40 }, // Professional Details
      { wch: 50 }, // Image URL
    ];

    ws["!cols"] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Directory");

    // Add filename with current date, filter info, and total count
    let fileName = `directory_${new Date().toISOString().split("T")[0]}`;

    // Add filter info to filename if filters are applied
    if (batchFilter || bloodGroupFilter || searchTerm) {
      fileName += "_filtered";
    }

    fileName += `_total_${filteredDirectories.length}.xlsx`;

    XLSX.writeFile(wb, fileName);

    // Show notification with count and filter info
    const filterInfo = [];
    if (batchFilter) filterInfo.push(`Group: ${batchFilter}`);
    if (bloodGroupFilter) filterInfo.push(`Blood: ${bloodGroupFilter}`);
    if (searchTerm) filterInfo.push(`Search: "${searchTerm}"`);

    const filterMessage =
      filterInfo.length > 0 ? ` (${filterInfo.join(", ")})` : "";

    showNotification(
      `Exported ${filteredDirectories.length} directory entries${filterMessage} to Excel`,
      "success"
    );
  };

  const handleView = (id) => {
    navigate(`/directory-details/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/edit-directory/${id}`);
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm("Are you sure you want to delete this directory entry?")
    )
      return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/directories/${id}`, {
        method: "DELETE",
        headers: getAuthHeader(),
      });

      if (response.status === 401) {
        logout();
        navigate("/login");
        return;
      }

      if (!response.ok) throw new Error("Failed to delete directory entry");

      setDirectories(directories.filter((d) => d.id !== id));
      showNotification("Directory entry deleted successfully", "success");
    } catch (error) {
      console.error("Delete failed:", error);
      showNotification(
        "Failed to delete directory entry: " + error.message,
        "error"
      );
    }
  };

  // Add this function to your component
  const downloadWord = async () => {
    try {
      showNotification("Preparing Word document with images...", "info");
  
      // Create HTML content for Word document
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Directory List</title>
          <style>
            body { 
              font-family: 'Segoe UI', Arial, sans-serif; 
              margin: 30px; 
            }
            .header { 
              margin-bottom: 30px; 
              padding: 20px; 
              background-color: #f8f9fa; 
              border-left: 5px solid #007bff;
            }
            h1 { 
              color: #333; 
              margin: 0 0 10px 0;
            }
            .filter-info { 
              color: #666;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              page-break-inside: avoid;
            }
            td {
              padding: 15px;
              vertical-align: top;
              border: 1px solid #e0e0e0;
            }
            .info-cell {
              width: 70%;
              background-color: white;
            }
            .image-cell {
              width: 30%;
              text-align: center;
              background-color: #fafafa;
            }
            .info-line {
              margin-bottom: 12px;
              font-size: 15px;
            }
            .info-label {
              font-weight: 600;
              color: #555;
              display: inline-block;
              width: 100px;
            }
            .info-value {
              color: #333;
            }
            .name-value {
              font-weight: 700;
              color: #000;
              font-size: 18px;
            }
            .image-container {
              width: 150px;
              height: 150px;
              margin: 0 auto;
              border-radius: 8px;
              overflow: hidden;
              border: 1px solid #ddd;
            }
            .image-container img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
            .no-image {
              width: 150px;
              height: 150px;
              margin: 0 auto;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-size: 48px;
              font-weight: bold;
              text-transform: uppercase;
              border-radius: 8px;
            }
            .arabic-text {
              font-family: 'Traditional Arabic', 'Times New Roman', serif;
              font-size: 20px;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              color: #888;
              font-size: 12px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Directory Information</h1>
            
      `;
  
      // Add filter information if any
      const filterInfo = [];
      if (batchFilter) filterInfo.push(`Batch: ${batchFilter}`);
      if (bloodGroupFilter) filterInfo.push(`Blood Group: ${bloodGroupFilter}`);
      if (searchTerm) filterInfo.push(`Search: "${searchTerm}"`);
  
      if (filterInfo.length > 0) {
        htmlContent += `<div class="filter-info"><strong>Filters Applied:</strong> ${filterInfo.join(
          " | "
        )}</div>`;
      }
  
      htmlContent += `</div>`;
  
      // Add each directory entry using table for guaranteed right-side image
      for (const person of filteredDirectories) {
        // Get image as base64 if available
        let imageBase64 = "";
        let initials = "";
        if (person.name) {
          initials = person.name
            .split(" ")
            .map((n) => n.charAt(0))
            .join("")
            .toUpperCase()
            .substring(0, 2);
        }
  
        if (person.imageUrl) {
          try {
            const imageUrl = person.imageUrl.startsWith("http")
              ? person.imageUrl
              : `${API_BASE_URL}${person.imageUrl}`;
  
            const response = await fetch(imageUrl, {
              headers: getAuthHeader(),
              mode: "cors",
              cache: "force-cache",
            });
  
            if (response.ok) {
              const blob = await response.blob();
              imageBase64 = await compressImage(blob, 300, 300, 0.7);
            }
          } catch (error) {
            console.error(`Failed to load image for ${person.name}:`, error);
          }
        }
  
        // Check if name contains Arabic characters
        const hasArabic = /[\u0600-\u06FF]/.test(person.name || "");
        const nameClass = hasArabic ? "arabic-text" : "";
  
        htmlContent += `
          <table>
            <tr>
              <!-- Left side: Information -->
              <td class="info-cell">
                ${person.name ? `
                  <div class="info-line">
                    <span class="info-label">Name:</span>
                    <span class="info-value name-value ${nameClass}"><strong>${escapeHtml(person.name)}</strong></span>
                  </div>
                ` : ''}
                
                ${person.batch ? `
                  <div class="info-line">
                    <span class="info-label">Group:</span>
                    <span class="info-value">${escapeHtml(person.batch)}</span>
                  </div>
                ` : ''}
                
                ${person.mobile ? `
                  <div class="info-line">
                    <span class="info-label">Contact No:</span>
                    <span class="info-value">${escapeHtml(person.mobile)}</span>
                  </div>
                ` : ''}
                
                ${person.email ? `
                  <div class="info-line">
                    <span class="info-label">Email:</span>
                    <span class="info-value">${escapeHtml(person.email)}</span>
                  </div>
                ` : ''}
                
                ${person.profession ? `
                  <div class="info-line">
                    <span class="info-label">Profession:</span>
                    <span class="info-value ${hasArabic ? 'arabic-text' : ''}">${escapeHtml(person.profession)}</span>
                  </div>
                ` : ''}
                
                ${person.officeAddress ? `
                  <div class="info-line">
                    <span class="info-label">Work Place:</span>
                    <span class="info-value">${escapeHtml(person.officeAddress)}</span>
                  </div>
                ` : ''}
                
                ${person.bloodGroup ? `
                  <div class="info-line">
                    <span class="info-label">Blood Group:</span>
                    <span class="info-value">${escapeHtml(person.bloodGroup)}</span>
                  </div>
                ` : ''}
              </td>
              
              <!-- Right side: Image -->
              <td class="image-cell">
                <div class="image-container">
                  ${
                    imageBase64
                      ? `<img src="${imageBase64}" alt="${escapeHtml(person.name)}" />`
                      : `<div class="no-image">${initials || ''}</div>`
                  }
                </div>
              </td>
            </tr>
          </table>
        `;
      }
  
      // Close HTML tags
      htmlContent += `
          <div class="footer">
            <p>Total Entries: ${filteredDirectories.length} | Generated on ${new Date().toLocaleDateString()}</p>
          </div>
        </body>
        </html>
      `;
  
      // Create and download Word document
      const blob = new Blob([htmlContent], {
        type: "application/msword",
      });
  
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `directory_${new Date().toISOString().split("T")[0]}.doc`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
  
      showNotification(
        `Word document with ${filteredDirectories.length} entries downloaded successfully`,
        "success"
      );
    } catch (error) {
      console.error("Word download failed:", error);
      showNotification(
        "Failed to generate Word document: " + error.message,
        "error"
      );
    }
  };
  
  // Helper function to compress images
  const compressImage = (blob, maxWidth = 300, maxHeight = 300, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (readerEvent) => {
        const image = new Image();
        image.onload = () => {
          // Calculate dimensions to fit in 150x150 box
          let width = image.width;
          let height = image.height;
          
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round(height * (maxWidth / width));
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round(width * (maxHeight / height));
              height = maxHeight;
            }
          }
          
          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(image, 0, 0, width, height);
          
          // Convert to compressed base64
          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        };
        image.onerror = reject;
        image.src = readerEvent.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };
  
  // Helper function to escape HTML
  const escapeHtml = (text) => {
    if (!text) return text;
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedDirectories = filteredDirectories.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredDirectories.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const getDirectoryTableHeaders = () => {
    if (isMobile) {
      return [
        { key: "info", label: "Info", className: "col-6" },
        { key: "actions", label: "Actions", className: "col-6" },
      ];
    } else if (isTablet) {
      return [
        { key: "photo", label: "Photo", className: "col-2" },
        { key: "basic", label: "Basic Info", className: "col-4" },
        { key: "contact", label: "Contact", className: "col-3" },
        { key: "actions", label: "Actions", className: "col-3" },
      ];
    }
    return [
      { key: "photo", label: "Photo", className: "" },
      { key: "basic", label: "Basic Info", className: "" },
      { key: "contact", label: "Contact", className: "" },
      { key: "professional", label: "Professional Info", className: "" },
      { key: "blood", label: "Blood Group", className: "" },
      { key: "actions", label: "Actions", className: "" },
    ];
  };

  const MobileDirectoryRow = ({ person }) => (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex align-items-start gap-3">
          <div style={{ width: "60px", flexShrink: 0 }}>
            <ImageWithActions
              imageUrl={person.imageUrl}
              name={person.name}
              altText={person.name}
              API_BASE_URL={API_BASE_URL}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h6 className="mb-1 text-truncate" style={{ maxWidth: "155px" }}>
              {person.name}
            </h6>
            <span className="badge bg-primary mb-2">{person.batch}</span>
          </div>
        </div>

        {/* <div className="small mt-2">
          <div>{person.mobile}</div>
          <div className="text-truncate" style={{ maxWidth: "160px" }}>
            {person.email}
          </div>
        </div> */}
        <div className="small mt-2">
          <span>
          {person.id}, {person.mobile}, {person.email}
          </span>
        </div>

        {person.profession && (
          <div className="mt-1 small">
            <strong>Profession:</strong> {person.profession}
          </div>
        )}

        {/* Full width buttons */}
        <div className="d-flex gap-2 mt-2 justify-content-center ">
          {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
            <div className="d-flex gap-2">
              <button
                onClick={() => handleDelete(person.id)}
                className="btn btn-outline-danger d-flex align-items-center justify-content-center flex-fill py-2"
                title="Delete"
              >
                <Trash2 size={16} className="mx-2" />
              </button>
              <button
                onClick={() => handleEdit(person.id)}
                className="btn btn-outline-warning d-flex align-items-center justify-content-center flex-fill py-2"
                title="Edit"
              >
                <Edit2 size={16} className="mx-2" />
              </button>
            </div>
          )}
          <button
            onClick={() => handleView(person.id)}
            className="btn btn-outline-primary d-flex align-items-center justify-content-center py-2"
            title="View"
          >
            <Eye size={16} className="mx-2" />
          </button>
        </div>
      </div>
    </div>
  );

  const TabletDirectoryRow = ({ person }) => (
    <tr>
      <td>
        <ImageWithActions
          imageUrl={person.imageUrl}
          name={person.name}
          altText={person.name}
          API_BASE_URL={API_BASE_URL}
        />
      </td>
      <td>
        <div className="d-flex flex-column">
        <strong>{person.name}</strong>
        <strong>{person.id}</strong>
          <div className="badge bg-primary">{person.batch}</div>
        </div>
      </td>
      <td>
        <div className="small">
          <div>{person.mobile}</div>
          <div className="text-truncate" style={{ maxWidth: "150px" }}>
            {person.email}
          </div>
        </div>
      </td>
      <td>
        <div className="d-flex flex-column gap-2">
          {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
            <button
              onClick={() => handleDelete(person.id)}
              className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          )}
          {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
            <button
              onClick={() => handleEdit(person.id)}
              className="btn btn-sm btn-outline-warning d-flex align-items-center justify-content-center"
              title="Edit"
            >
              <Edit2 size={16} />
            </button>
          )}
          <button
            onClick={() => handleView(person.id)}
            className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
            title="View"
          >
            <Eye size={14} />
          </button>
        </div>
      </td>
    </tr>
  );

  if (loading && directories.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading directory...</p>
      </div>
    );
  }

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        {/* Header with Actions */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
          <h2 className="mb-0">Directory List</h2>
          <div className="d-flex flex-wrap gap-2">
            <button
              className="btn btn-outline-secondary d-md-none"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <Filter size={18} />
              Filters
            </button>

            <button
              className="btn btn-success d-flex align-items-center gap-2"
              onClick={downloadExcel}
            >
              <Download size={18} />
              <span className="d-none d-sm-inline">Export</span>
            </button>

            <button
              className="btn btn-warning d-flex align-items-center gap-2"
              onClick={downloadWord}
              disabled={refreshing}
            >
              <FileText size={18} />
              <span className="d-none d-sm-inline">Word</span>
            </button>

            <button
              className="btn btn-outline-primary d-flex align-items-center gap-2"
              onClick={fetchDirectories}
              disabled={refreshing}
            >
              <RefreshCw size={18} className={refreshing ? "spin" : ""} />
              <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div
          className={`card mb-3 ${isMobile && !isFilterOpen ? "d-none" : ""}`}
        >
          <div className="card-body">
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="input-group">
                  <span className="input-group-text">
                    <Search size={16} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search directory..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchTerm("")}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-3">
                <select
                  className="form-select"
                  value={batchFilter}
                  onChange={(e) => setBatchFilter(e.target.value)}
                >
                  <option value="">All Groups</option>
                  {uniqueBatches.map((batch) => (
                    <option key={batch} value={batch}>
                      {batch}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-3">
                <select
                  className="form-select"
                  value={bloodGroupFilter}
                  onChange={(e) => setBloodGroupFilter(e.target.value)}
                >
                  <option value="">All Blood Groups</option>
                  {uniqueBloodGroups.map((group) => (
                    <option key={group} value={group}>
                      {group}
                    </option>
                  ))}
                </select>
              </div>

              {(batchFilter || bloodGroupFilter || searchTerm) && (
                <div className="col-12">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                      setBatchFilter("");
                      setBloodGroupFilter("");
                      setSearchTerm("");
                    }}
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pagination Controls - Top */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <span className="small text-muted">Show:</span>
              <select
                className="form-select form-select-sm"
                style={{ width: "auto" }}
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              {/* <span className="small text-muted">entries</span> */}
            </div>
            <div className="small text-muted">
              Showing {indexOfFirstItem + 1} to{" "}
              {Math.min(indexOfLastItem, filteredDirectories.length)} of{" "}
              {filteredDirectories.length} entries
            </div>
          </div>
        </div>

        {/* Directory Content */}
        {paginatedDirectories.length > 0 ? (
          <>
            {/* Mobile View */}
            {isMobile ? (
              <div>
                {paginatedDirectories.map((person) => (
                  <MobileDirectoryRow key={person.id} person={person} />
                ))}
              </div>
            ) : /* Tablet View */
            isTablet ? (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      {getDirectoryTableHeaders().map((header) => (
                        <th key={header.key} className={header.className}>
                          {header.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedDirectories.map((person) => (
                      <TabletDirectoryRow key={person.id} person={person} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Desktop View */
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      {getDirectoryTableHeaders().map((header) => (
                        <th key={header.key}>{header.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedDirectories.map((person) => (
                      <tr key={person.id}>
                        <td style={{ width: "80px" }}>
                          <ImageWithActions
                            imageUrl={person.imageUrl}
                            name={person.name}
                            altText={person.name}
                            API_BASE_URL={API_BASE_URL}
                          />
                        </td>
                        <td>
                          <div>
                            <div>
                              <strong>{person.name}</strong>
                            </div>
                            <div className="badge bg-primary">
                              {person.batch}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="small">
                            <div>{person.mobile}</div>
                            <div>{person.email}</div>
                            <div className="text-muted">
                              {person.permanentAddress}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong>{person.profession}</strong>
                            <div className="small">{person.officeAddress}</div>
                          </div>
                        </td>
                        <td>
                          {person.bloodGroup && (
                            <span
                              className={`badge ${
                                person.bloodGroup?.includes("+")
                                  ? "bg-danger"
                                  : "bg-warning"
                              }`}
                            >
                              {person.bloodGroup}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            {(user?.role === "ADMIN" ||
                              user?.role === "EDITOR") && (
                              <button
                                onClick={() => handleDelete(person.id)}
                                className="btn btn-sm btn-outline-danger"
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                            {(user?.role === "ADMIN" ||
                              user?.role === "EDITOR") && (
                              <button
                                onClick={() => handleEdit(person.id)}
                                className="btn btn-sm btn-outline-warning "
                                title="Edit"
                              >
                                <Edit2 size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => handleView(person.id)}
                              className="btn btn-sm btn-outline-primary"
                              title="View"
                            >
                              <Eye size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls - Bottom */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                <div className="small text-muted">
                  Page {currentPage} of {totalPages}
                </div>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNumber;
                      if (totalPages <= 5) {
                        pageNumber = i + 1;
                      } else if (currentPage <= 3) {
                        pageNumber = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + i;
                      } else {
                        pageNumber = currentPage - 2 + i;
                      }

                      return (
                        <li
                          key={pageNumber}
                          className={`page-item ${
                            currentPage === pageNumber ? "active" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        </li>
                      );
                    })}

                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-5">
            <div className="text-muted">
              <i className="bi bi-people display-6 d-block mb-2"></i>
              No directory entries found
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectoryList;
